<template>
    <div
        v-if="isRequired"
        class="mb-6 flex items-start space-x-3 rounded-lg border border-blue-200 bg-blue-50 p-4 text-blue-900 shadow-sm">
        <svg
            class="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor">
            <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>

        <div class="space-y-1 text-sm leading-relaxed">
            <!-- LINE 1: Title -->
            <p class="font-bold">
                {{
                    $t('investment_process.pre_emptive_rights_notice_title', { default: 'Bezugssrechte erforderlich' })
                }}
            </p>

            <!-- LINE 2: Current Holdings -->
            <p>
                {{
                    $t('investment_process.pre_emptive_rights_notice_body', {
                        default: 'Eine Investition in diese Kampagne erfordert Bezugssrechte. Sie besitzen derzeit:',
                    })
                }}
                <span class="font-bold text-blue-900">
                    <template v-if="isLoading">...</template>
                    <template v-else>{{ availableRights }}</template>
                </span>
                {{ $t('investment_process.pre_emptive_rights_units', { default: 'Recht(e)' }) }}.
            </p>

            <!-- LINE 3: Ratio Description (Ratio >= 1 or Ratio < 1) -->
            <p>
                <template v-if="subscriptionRatio >= 1">
                    1 Anteil erfordert <span class="font-bold">{{ subscriptionRatio }}</span> Bezugssrecht(e).
                </template>
                <template v-else>
                    <span class="font-bold">{{ sharesPerSingleRight }}</span> Anteile erfordern
                    <span class="font-bold">1</span> Bezugssrecht.
                </template>
            </p>

            <!-- LINE 4: Selected Amount vs Required Rights Calculation -->
            <p v-if="cleanSharesNumber > 0">
                Aktuelle Auswahl: <span class="font-bold">{{ cleanSharesNumber }}</span> Anteile =
                <span class="font-bold">{{ requiredRights }}</span> Bezugssrecht(e)
            </p>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue'
import { roundToPrecision } from '../../../utilities/helpers'

const PRE_EMPTIVE_RIGHTS_DECIMALS = 5

const props = defineProps({
    isRequired: {
        type: Boolean,
        default: false,
    },
    isLoading: {
        type: Boolean,
        default: false,
    },
    availableRights: {
        type: Number,
        default: 0,
    },
    subscriptionRatio: {
        type: Number,
        default: 1,
    },
    shares: {
        type: [Number, String],
        default: 0,
    },
})

// Clean integer representation of requested shares
const cleanSharesNumber = computed(() => {
    const rawVal = Number(props.shares)
    if (isNaN(rawVal) || rawVal <= 0) return 0
    return Math.round(rawVal)
})

// Total required pre-emptive rights for selected shares
const requiredRights = computed(() => {
    const ratio = Number(props.subscriptionRatio) <= 0 ? 1 : Number(props.subscriptionRatio)
    return roundToPrecision(cleanSharesNumber.value * ratio, PRE_EMPTIVE_RIGHTS_DECIMALS)
})

// Shares per 1 right when ratio < 1
const sharesPerSingleRight = computed(() => {
    const ratio = Number(props.subscriptionRatio)
    return ratio > 0 ? Math.round(1 / ratio) : 1
})
</script>
